/**
 * Tax & Payroll Tools
 * Unified wrapper combining Income Tax Estimator and Payroll Calculator.
 * CA users see both tabs; US users see Income Tax Estimator only.
 */

import React, { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useCountry } from '../contexts/CountryContext';
import { SharedModal } from './shared/SharedModal';
import IncomeTaxCalculator from './IncomeTaxCalculator';
import PayrollCalculator from './PayrollCalculator';
import '../styles/TaxToolsApp.css';

type ActiveTab = 'estimator' | 'payroll';

const TaxToolsApp: React.FC = () => {
  const { isCanada } = useCountry();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab from URL or default to estimator
  const tabParam = searchParams.get('tab');
  const initialTab: ActiveTab = (tabParam === 'payroll' && isCanada) ? 'payroll' : 'estimator';
  const [activeTab, setActiveTab] = useState<ActiveTab>(initialTab);

  // Disclaimer state - session only, shown once before first Calculate
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [pendingCalculate, setPendingCalculate] = useState<(() => void) | null>(null);

  const handleTabChange = (tab: ActiveTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  // Called by child calculators before running their calculate
  const onBeforeCalculate = useCallback((calculateFn: () => void) => {
    if (disclaimerAccepted) {
      calculateFn();
    } else {
      setPendingCalculate(() => calculateFn);
      setShowDisclaimer(true);
    }
  }, [disclaimerAccepted]);

  const handleAcceptDisclaimer = () => {
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);
    if (pendingCalculate) {
      pendingCalculate();
      setPendingCalculate(null);
    }
  };

  const title = isCanada ? 'Tax & Payroll Tools' : 'Income Tax Estimator';
  const subtitle = isCanada
    ? 'Estimate income tax or calculate payroll deductions for all provinces'
    : 'Estimate your federal and state income tax';

  return (
    <div className="tax-tools-app">
      <div className="tax-tools-header">
        <h1>{title}</h1>
        <p>{subtitle}</p>
      </div>

      {/* Tab bar - only show for CA users */}
      {isCanada && (
        <div className="tax-tools-tabs">
          <button
            className={`tax-tools-tab ${activeTab === 'estimator' ? 'active' : ''}`}
            onClick={() => handleTabChange('estimator')}
          >
            Income Tax Estimator
          </button>
          <button
            className={`tax-tools-tab ${activeTab === 'payroll' ? 'active' : ''}`}
            onClick={() => handleTabChange('payroll')}
          >
            Payroll Calculator
          </button>
        </div>
      )}

      {/* Render active calculator */}
      {activeTab === 'estimator' ? (
        <IncomeTaxCalculator embedded onBeforeCalculate={onBeforeCalculate} />
      ) : (
        <PayrollCalculator embedded onBeforeCalculate={onBeforeCalculate} />
      )}

      {/* Disclaimer Modal */}
      <SharedModal
        isOpen={showDisclaimer}
        onClose={() => { setShowDisclaimer(false); setPendingCalculate(null); }}
        title="Important Disclaimer"
        size="small"
        closeOnOverlayClick={false}
        closeOnEscape={false}
        showCloseButton={false}
      >
        <div className="disclaimer-content">
          <p>
            These calculations are <strong>illustrative estimates only</strong> and should not be
            relied upon for tax filing or payroll processing.
          </p>
          <ul>
            <li>Always verify with official CRA payroll deduction tables (T4127)</li>
            <li>Consult a qualified tax professional for actual tax filings</li>
            <li>Rates and thresholds may change — check current CRA/IRS publications</li>
          </ul>
          <p>
            By continuing, you acknowledge that these results are for informational purposes only.
          </p>
          <button className="disclaimer-accept-btn" onClick={handleAcceptDisclaimer}>
            I Understand — Continue
          </button>
        </div>
      </SharedModal>
    </div>
  );
};

export default TaxToolsApp;
